import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { prescriptionAPI } from "@/lib/prescription-api";
import { useToast } from "@/components/ui/use-toast";
import { AlertCircle, Loader2, ZoomIn, ZoomOut, Send, X } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PX_PER_MM = 3.7795275591; // 96 DPI
const THUMB_SCALE = 0.2; // ~159×225px per thumbnail
const SCROLL_PADDING_PX = 64; // matches `py-16` on the inner scroll content

interface MissingField {
  label: string;
  hint: string;
}

interface PrescriptionPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  parentType?: "prescription" | "meal_plan";
  missingFields?: MissingField[];
}

export const PrescriptionPreviewDialog = ({
  open,
  onOpenChange,
  appointmentId,
  parentType = "prescription",
  missingFields = [],
}: PrescriptionPreviewDialogProps) => {
  const isNutrition = parentType === "meal_plan";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [scale, setScale] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [showMissingFields, setShowMissingFields] = useState(false);

  // Drag to scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Iframe content sizing: measure rendered HTML height so the wrapper grows
  // to fit it (avoids a nested scrollbar inside the iframe).
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [contentHeightMm, setContentHeightMm] = useState(A4_HEIGHT_MM);
  const [activePage, setActivePage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(contentHeightMm / A4_HEIGHT_MM));

  const handleIframeLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const measure = () => {
      const heightPx = Math.max(
        doc.documentElement.scrollHeight,
        doc.body?.scrollHeight ?? 0
      );
      const heightMm = heightPx / PX_PER_MM;
      setContentHeightMm(Math.max(A4_HEIGHT_MM, heightMm));
    };
    measure();
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(measure);
    resizeObserverRef.current.observe(doc.documentElement);
  };

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  // Fetch Preview HTML
  const {
    data: htmlContent,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      isNutrition ? "nutrition-preview" : "prescription-preview",
      appointmentId,
    ],
    queryFn: () =>
      isNutrition
        ? prescriptionAPI.verifyNutritionReport(appointmentId)
        : prescriptionAPI.verifyPrescription(appointmentId),
    enabled: open,
    staleTime: 0,
  });

  // Publish Mutation
  const publishMutation = useMutation({
    mutationFn: () =>
      isNutrition
        ? prescriptionAPI.publishNutritionReport(appointmentId)
        : prescriptionAPI.publishPrescription(appointmentId),
    onSuccess: () => {
      toast({
        title: isNutrition ? "Nutrition Report Sent" : "Prescription Sent",
        description: isNutrition
          ? "The nutrition report has been successfully published and sent to the patient."
          : "The prescription has been successfully published and sent to the patient.",
      });
      onOpenChange(false);
      queryClient.invalidateQueries({
        queryKey: ["appointment-details", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["appointmentDetails", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["staff-appointments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["appointments"],
      });
      navigate("/");
    },
    onError: () => {
      toast({
        title: "Error",
        description: isNutrition
          ? "Failed to send nutrition report. Please try again."
          : "Failed to send prescription. Please try again.",
        variant: "destructive",
      });
      setIsSending(false);
    },
  });

  const handleSend = () => {
    if (missingFields.length > 0) {
      setShowMissingFields(true);
      return;
    }
    setIsSending(true);
    setCountdown(5);
  };

  const handleCancelSend = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
    setIsSending(false);
  };

  // Countdown Logic
  useEffect(() => {
    if (countdown === null) return;
    if (countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else {
      publishMutation.mutate();
      setCountdown(null);
    }
    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [countdown, publishMutation]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setScale(1);
      setContentHeightMm(A4_HEIGHT_MM);
      setActivePage(0);
      setShowMissingFields(false);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      handleCancelSend();
    }
  }, [open]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  // Drag Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setStartY(e.pageY - scrollContainerRef.current.offsetTop);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setScrollTop(scrollContainerRef.current.scrollTop);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const y = e.pageY - scrollContainerRef.current.offsetTop;
    const walkX = (x - startX) * 1.5;
    const walkY = (y - startY) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walkX;
    scrollContainerRef.current.scrollTop = scrollTop - walkY;
  };

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const adjusted = Math.max(0, el.scrollTop - SCROLL_PADDING_PX);
    const pageHeightPx = A4_HEIGHT_MM * PX_PER_MM * scale;
    if (pageHeightPx <= 0) return;
    const idx = Math.round(adjusted / pageHeightPx);
    setActivePage((prev) => {
      const clamped = Math.max(0, Math.min(totalPages - 1, idx));
      return prev === clamped ? prev : clamped;
    });
  }, [scale, totalPages]);

  const scrollToPage = (pageIdx: number) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const pageHeightPx = A4_HEIGHT_MM * PX_PER_MM * scale;
    el.scrollTo({
      top: pageIdx * pageHeightPx + SCROLL_PADDING_PX,
      behavior: "smooth",
    });
    setActivePage(pageIdx);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-none sm:max-w-none w-screen h-screen top-0 left-0 translate-x-0 translate-y-0 rounded-none border-0 p-0 gap-0 flex flex-col bg-background [&>button.absolute]:hidden"
          onPointerDownOutside={(e) => isSending && e.preventDefault()}
          onEscapeKeyDown={(e) => isSending && e.preventDefault()}
        >
          <TooltipProvider delayDuration={200}>
            <DialogHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between space-y-0 bg-card shrink-0">
              <DialogTitle className="text-foreground">
                {isNutrition
                  ? "Nutrition Report Preview"
                  : "Prescription Preview"}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {/* Zoom Controls */}
                <div className="flex items-center border border-border rounded-md mr-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        className="h-8 w-8"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Zoom out</TooltipContent>
                  </Tooltip>
                  <Separator orientation="vertical" className="h-5 bg-border" />
                  <span className="w-12 text-center text-sm tabular-nums text-muted-foreground">
                    {Math.round(scale * 100)}%
                  </span>
                  <Separator orientation="vertical" className="h-5 bg-border" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleZoomIn}
                        disabled={scale >= 2}
                        className="h-8 w-8"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Zoom in</TooltipContent>
                  </Tooltip>
                </div>

                {!isSending && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onOpenChange(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Close</TooltipContent>
                  </Tooltip>
                )}

                <Button
                  onClick={handleSend}
                  className="w-32 gap-2 ml-2"
                  disabled={isLoading || isError || isSending}
                >
                  <Send className="h-4 w-4" />
                  Send
                </Button>
              </div>
            </DialogHeader>
          </TooltipProvider>

          <div className="flex-1 flex overflow-hidden relative bg-muted">
            {/* Countdown Overlay */}
            {isSending && (
              <div className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center text-foreground animate-in fade-in duration-300">
                <div className="text-center space-y-8">
                  <h2 className="text-3xl font-semibold">
                    {isNutrition
                      ? "Sending Nutrition Report..."
                      : "Sending Prescription..."}
                  </h2>
                  <div className="relative flex items-center justify-center">
                    <div className="text-9xl font-bold font-mono tabular-nums tracking-tighter text-primary">
                      {countdown}
                    </div>
                    <div className="absolute -bottom-8 z-eyebrow text-muted-foreground">
                      Seconds Remaining
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="lg"
                    onClick={handleCancelSend}
                    className="min-w-[200px] h-14 text-lg mt-8"
                  >
                    Cancel Sending
                  </Button>
                  <p className="z-body text-muted-foreground max-w-md mx-auto">
                    {isNutrition
                      ? "The nutrition report will be published and sent to the patient automatically when the timer expires."
                      : "The prescription will be published and sent to the patient automatically when the timer expires."}
                  </p>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : isError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-destructive gap-2">
                <p>Failed to load preview.</p>
                <Button
                  variant="outline"
                  onClick={() =>
                    queryClient.invalidateQueries({
                      queryKey: [
                        isNutrition
                          ? "nutrition-preview"
                          : "prescription-preview",
                        appointmentId,
                      ],
                    })
                  }
                >
                  Retry
                </Button>
              </div>
            ) : (
              <>
                {/* Left: Page thumbnails */}
                <aside className="w-[220px] shrink-0 border-r border-border bg-card flex flex-col">
                  <div className="px-3 py-2 border-b border-border text-xs font-medium text-muted-foreground">
                    {totalPages} {totalPages === 1 ? "page" : "pages"}
                  </div>
                  <ScrollArea className="flex-1">
                    <div className="p-3 space-y-3">
                      {Array.from({ length: totalPages }).map((_, idx) => (
                        <ThumbnailPage
                          key={idx}
                          pageIdx={idx}
                          htmlContent={htmlContent ?? ""}
                          contentHeightMm={contentHeightMm}
                          isActive={activePage === idx}
                          onClick={() => scrollToPage(idx)}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                </aside>

                {/* Right: Document */}
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className={cn(
                    "flex-1 overflow-auto",
                    isDragging ? "cursor-grabbing select-none" : "cursor-grab"
                  )}
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeave}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                >
                  {/* Inner padded wrapper: horizontal padding only.
                    `flow-root` creates a new block formatting context so the
                    document's vertical `my-*` margin stays inside this wrapper
                    (no margin collapse with the scroll container).
                    `min-w-fit` keeps horizontal padding intact when the page
                    is wider than the viewport at high zoom. */}
                  <div className="min-w-fit px-8 flow-root">
                    <div
                      className="relative transition-all duration-200 ease-in-out mx-auto my-16"
                      style={{
                        width: `${A4_WIDTH_MM * scale}mm`,
                        height: `${contentHeightMm * scale}mm`,
                      }}
                    >
                      <div
                        style={{
                          transform: `scale(${scale})`,
                          transformOrigin: "top left",
                          width: `${A4_WIDTH_MM}mm`,
                          height: `${contentHeightMm}mm`,
                          transition: isDragging
                            ? "none"
                            : "transform 0.2s ease-in-out",
                        }}
                        className="bg-white shadow-xl ring-1 ring-border pointer-events-none absolute top-0 left-0"
                      >
                        {/* Document is printed paper — stays white in any theme,
                          same as PdfViewer page rendering. */}
                        <iframe
                          ref={iframeRef}
                          srcDoc={htmlContent}
                          onLoad={handleIframeLoad}
                          className="w-full h-full border-none pointer-events-none"
                          title={
                            isNutrition
                              ? "Nutrition Report Preview"
                              : "Prescription Preview"
                          }
                          sandbox="allow-same-origin"
                          scrolling="no"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showMissingFields} onOpenChange={setShowMissingFields}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
              <AlertCircle className="size-5 text-destructive" />
              Missing required fields
            </DialogTitle>
            <DialogDescription>
              Fill in the following before sending the nutrition report.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 py-2">
            {missingFields.map((field) => (
              <li
                key={field.label}
                className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3"
              >
                <span className="mt-0.5 size-1.5 rounded-full bg-destructive shrink-0" />
                <div className="min-w-0">
                  <p className="z-body text-foreground">{field.label}</p>
                  <p className="z-hint text-muted-foreground">{field.hint}</p>
                </div>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setShowMissingFields(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface ThumbnailPageProps {
  pageIdx: number;
  htmlContent: string;
  contentHeightMm: number;
  isActive: boolean;
  onClick: () => void;
}

const ThumbnailPage = ({
  pageIdx,
  htmlContent,
  contentHeightMm,
  isActive,
  onClick,
}: ThumbnailPageProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full group focus:outline-none"
      aria-label={`Go to page ${pageIdx + 1}`}
      aria-current={isActive ? "page" : undefined}
    >
      <div
        className={cn(
          "mx-auto bg-white relative overflow-hidden rounded-md transition-all",
          "ring-2 ring-offset-2 ring-offset-card",
          isActive
            ? "ring-primary shadow-md"
            : "ring-transparent shadow-sm group-hover:ring-border"
        )}
        style={{
          width: `${A4_WIDTH_MM * THUMB_SCALE}mm`,
          height: `${A4_HEIGHT_MM * THUMB_SCALE}mm`,
        }}
      >
        <div
          style={{
            transform: `scale(${THUMB_SCALE})`,
            transformOrigin: "top left",
            width: `${A4_WIDTH_MM}mm`,
            height: `${contentHeightMm}mm`,
            position: "absolute",
            top: `-${pageIdx * A4_HEIGHT_MM * THUMB_SCALE}mm`,
            left: 0,
          }}
        >
          <iframe
            srcDoc={htmlContent}
            style={{
              width: `${A4_WIDTH_MM}mm`,
              height: `${contentHeightMm}mm`,
              border: "none",
              pointerEvents: "none",
            }}
            scrolling="no"
            sandbox="allow-same-origin"
            tabIndex={-1}
            title={`Page ${pageIdx + 1} thumbnail`}
          />
        </div>
      </div>
      <div
        className={cn(
          "text-center text-xs py-1.5 font-medium",
          isActive ? "text-primary" : "text-muted-foreground"
        )}
      >
        Page {pageIdx + 1}
      </div>
    </button>
  );
};
